// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Remembered pairings. A pairing is a (room, passphrase, name) triple that both
 * of your devices keep in localStorage so that after the first QR scan, picking
 * "My phone" from a list reconnects in one tap. The passphrase never leaves the
 * device — it lives here and in the link hash only.
 *
 * All functions are pure over an injected storage-like object, so they are
 * unit-tested without a browser.
 */
import type { Pairing } from './types.ts';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const KEY = 'beamwell.pairings';
const MAX_PAIRINGS = 20;

/** A random id for a stored pairing entry. */
export function makePairingId(): string {
  const b = new Uint8Array(6);
  globalThis.crypto.getRandomValues(b);
  let out = '';
  for (const x of b) out += x.toString(16).padStart(2, '0');
  return out;
}

/** Build a pairing record. */
export function makePairing(room: string, pass: string, name: string, at: number, id?: string): Pairing {
  return { id: id ?? makePairingId(), room, pass, name: name.trim() || 'Paired device', createdAt: at, lastUsedAt: at };
}

/** Load the remembered pairings (newest-used first). Tolerates corrupt storage. */
export function loadPairings(store: StorageLike): Pairing[] {
  try {
    const raw = store.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const valid = parsed.filter(isPairing);
    return sortByUsed(valid);
  } catch {
    return [];
  }
}

/** Persist a pairing list, capped and sorted. */
export function savePairings(store: StorageLike, list: Pairing[]): void {
  const trimmed = sortByUsed(list).slice(0, MAX_PAIRINGS);
  store.setItem(KEY, JSON.stringify(trimmed));
}

/**
 * Insert or update a pairing by ROOM (a device re-pairs into the same room), then
 * return the new list. If a name is given it replaces the stored one.
 */
export function upsertPairing(list: Pairing[], p: Pairing): Pairing[] {
  const existing = list.find((x) => x.room === p.room);
  let next: Pairing[];
  if (existing) {
    next = list.map((x) =>
      x.room === p.room
        ? { ...x, pass: p.pass, name: p.name || x.name, lastUsedAt: Math.max(p.lastUsedAt, x.lastUsedAt) }
        : x,
    );
  } else {
    next = [...list, p];
  }
  return sortByUsed(next);
}

/** Remove a pairing by id. */
export function removePairing(list: Pairing[], id: string): Pairing[] {
  return list.filter((x) => x.id !== id);
}

/** Bump a pairing's lastUsedAt (by room), returning the re-sorted list. */
export function touchPairing(list: Pairing[], room: string, at: number): Pairing[] {
  return sortByUsed(list.map((x) => (x.room === room ? { ...x, lastUsedAt: at } : x)));
}

function sortByUsed(list: Pairing[]): Pairing[] {
  return [...list].sort((a, b) => b.lastUsedAt - a.lastUsedAt);
}

function isPairing(x: unknown): x is Pairing {
  if (!x || typeof x !== 'object') return false;
  const p = x as Record<string, unknown>;
  return (
    typeof p.id === 'string' &&
    typeof p.room === 'string' &&
    typeof p.pass === 'string' &&
    typeof p.name === 'string' &&
    typeof p.createdAt === 'number' &&
    typeof p.lastUsedAt === 'number'
  );
}
